import Quill from 'quill/core';
import React, { useEffect, useState } from 'react';
import { IS_EXTENSION } from '../../../constants/platform';
import type { DocumentType } from '../../../graphql/generated/gql';
import { gql } from '../../../graphql/generated/gql';
import useIsPublicView from '../../../utils/useIsPublicView';
import { useQuill } from '../../QuillContext';
import AttachmentMention from '../../formats/AttachmentMention';
import AttachmentMentionToolbar from './AttachmentMentionToolbar';
import EditModeToolbar from './EditModeToolbar';
import ImportedToolbar from './ImportedToolbar';
import LinkToolbar from './LinkToolbar';
import MentionToolbar from './MentionToolbar';
import ReadModeToolbar from './ReadModeToolbar';
import ToolbarPopOver from './ToolbarPopOver';
import type { ToolbarObjectButtonOptions } from './buttons/ToolbarObjectButton';

const Parchment = Quill.import('parchment');

const POST_FRAGMENT = gql(`
  fragment EditorToolbar_post on PublicPostFields {
    id
    ... on Post {
      externalPost {
        id
        ...ImportedToolbar_externalPost
      }
    }
  }
`);

interface EditorToolbarProps {
  post?: DocumentType<typeof POST_FRAGMENT> | null;
  readOnly: boolean;
  showInTitleLine?: boolean;
  objectButtonOptions?: ToolbarObjectButtonOptions;
  portal?: HTMLElement | null;
}

const EditorToolbar = ({
  post,
  readOnly,
  showInTitleLine,
  objectButtonOptions,
  portal,
}: EditorToolbarProps) => {
  const {
    activeBlot,
    quill,
    selection,
    editingLink: isEditingLink,
    editingLinkChange,
  } = useQuill();
  const { formats } = selection;
  const [isLinkSelected, setIsLinkSelected] = useState(false);
  const isPublicView = useIsPublicView();

  useEffect(() => {
    setIsLinkSelected(false);
  }, [selection.range]);

  // ToolbarPopOver relies on Redux so it doesn't work on extension
  if (!quill || IS_EXTENSION) return null;

  return (
    <ToolbarPopOver showInTitleLine={showInTitleLine} portal={portal}>
      {({ lines, range, isInTitleLine, closeToolbar }) => {
        // LinkToolbar will display when:
        // 1. User is editing the link
        //    (either by creating a new link or clicking the edit icon on LinkToolbar);
        // 2. The selection is a link **and**;
        //    a. The selection is collapsed and is in edit mode,
        //    b. or, User clicked the link icon on EditModeToolbar;
        if (
          isEditingLink ||
          (formats.link && ((!range.length && !readOnly) || isLinkSelected))
        ) {
          return (
            <LinkToolbar
              quill={quill}
              onLinkChange={(value) =>
                quill.format('link', value, Quill.sources.USER)
              }
            />
          );
        }

        if (!range.length) return null;
        if (activeBlot?.statics?.blotName === 'mention')
          // @ts-expect-error Mention is an inline blot. Should be resolved when Quill is migrated to TypeScript
          return <MentionToolbar blot={activeBlot} />;

        if (activeBlot instanceof AttachmentMention) {
          return <AttachmentMentionToolbar blot={activeBlot} />;
        }

        if (lines.every((line) => line instanceof Parchment.EmbedBlot))
          return null;
        if (!quill.getText(range.index, range.length).replace(/\n/g, '').length)
          return null;

        if (post && 'externalPost' in post && post.externalPost) {
          return <ImportedToolbar externalPost={post.externalPost} />;
        }
        if (readOnly) {
          if (isPublicView) {
            return null;
          }
          return (
            <ReadModeToolbar formats={formats} closeToolbar={closeToolbar} />
          );
        }
        return (
          <EditModeToolbar
            multiline={lines.length > 1}
            inTitleLine={isInTitleLine}
            objectButtonOptions={objectButtonOptions}
            closeToolbar={closeToolbar}
            onLinkSelected={() => {
              if (!formats.link) {
                editingLinkChange(true);
              }
              setIsLinkSelected(true);
            }}
          />
        );
      }}
    </ToolbarPopOver>
  );
};

export default EditorToolbar;
